/**
 * MailRenderPreview + MailFormatSelect - the operator composer's view of a
 * mail exactly as it will be sent (OCP-928).
 *
 * The HTML and the text part come from the API's render endpoints
 * (`/v1/admin/mail/operator/render`, `/v1/admin/mail/announcements/render`),
 * the same renderer the send uses - the browser never re-implements the
 * Markdown or the sanitizer, so the preview cannot drift from the mail.
 *
 * The HTML is operator-authored markup after the server-side sanitizer.
 * It is still shown the OCP-544 way, never injected into the admin
 * document: an iframe with an EMPTY `sandbox` (opaque origin, no scripts,
 * no forms, no navigation) and `srcdoc`, which inherits the admin CSP.
 */
export type MailBodyFormat = 'text' | 'markdown' | 'html';

export interface RenderedMailParts {
  subject: string;
  html: string;
  text: string;
}

export const MAIL_FORMAT_HINT: Record<MailBodyFormat, string> = {
  text: 'Plain text: blank line = new paragraph, everything shown as typed.',
  markdown: 'Markdown: **bold**, *italic*, `code`, [link](https://...), - or 1. lists, ## heading, > quote, ---. Raw HTML is shown as text.',
  html: 'HTML: sanitized (no script, style, iframe, form or on* attributes; links only https: and mailto:), rendered inside the orboto mail shell.',
};

export function MailFormatSelect({
  value,
  onChange,
  className,
}: {
  value: MailBodyFormat;
  onChange: (f: MailBodyFormat) => void;
  className?: string;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value as MailBodyFormat)}
      aria-label="Body format"
      className={className ?? 'rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1 text-xs'}
    >
      <option value="text">plain text</option>
      <option value="markdown">Markdown</option>
      <option value="html">HTML</option>
    </select>
  );
}

export function MailRenderPreview({ mail, label, testId }: { mail: RenderedMailParts; label: string; testId: string }) {
  return (
    <div className="space-y-1" data-testid={testId}>
      <div className="text-[11px] font-medium uppercase tracking-wide text-gray-500">
        {label}: <span className="normal-case font-normal text-gray-700 dark:text-gray-300">{mail.subject}</span>
      </div>
      <iframe
        title={`${label} - HTML part`}
        data-testid={`${testId}-html`}
        sandbox=""
        referrerPolicy="no-referrer"
        srcDoc={mail.html}
        className="h-96 w-full rounded border border-gray-200 dark:border-gray-800 bg-white"
      />
      <div className="text-[11px] text-gray-500">Text part</div>
      <pre
        data-testid={`${testId}-text`}
        className="max-h-64 overflow-auto whitespace-pre-wrap rounded bg-gray-50 dark:bg-gray-800 p-2 text-xs text-gray-800 dark:text-gray-200"
      >
        {mail.text}
      </pre>
    </div>
  );
}
